// Simulator.jsx — Two clean tools for marketing:
//   1) Offers Simulator: discount % + duration → suggested sale price + units-to-sell
//      to hit the desired revenue target. No clutter, no elasticity, no ROI math.
//   2) Combo Simulator: pick 2 (or 3) products → total cost, suggested combo price
//      with a desired margin, expected profit, and units-needed-to-hit-target.
//
// Both modes are tuned for *marketing-team usage*: the inputs are 1-2 fields
// and the outputs are concrete sale plans they can pitch directly.

import React, { useEffect, useMemo, useState } from "react";
import { fm } from "./lib/analytics";
import { downloadCSV } from "./lib/export";
import { api } from "./lib/api";
import { showToast } from "./lib/theme";
import { offerMath, comboMath, DEFAULT_VAT_RATE } from "./lib/simulatorMath";
import { formatKSADate, ksaAddDays, ksaToday } from "./lib/ksaTime";

const fmt = (n, d = 2) => (Number(n) || 0).toFixed(d);

// Submit a marketing plan into the approval workflow:
//   submitter → operations → CEO → published as campaign.
// We POST the full simulator state in `payload` so the approvers can see the
// exact numbers the marketing team produced. `summary` is the one-liner that
// shows up in the approver's inbox.
async function submitPlanForApproval({ plan_type, title, summary, payload, cost_sar }) {
  const today = new Date().toISOString().slice(0, 10);
  const inThirtyDays = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  try {
    await api.post("/combo-approvals", {
      plan_type, title, summary, payload,
      cost_sar: Number(cost_sar) || 0,
      start_date: today, end_date: inThirtyDays,
    });
    showToast({ kind: "success", message: "📤 تم رفع الخطة لاعتماد مدير التشغيل" });
    return true;
  } catch (e) {
    const d = e?.response?.data?.detail;
    const msg = typeof d === "string" ? d : (e?.message || "تعذّر الرفع");
    showToast({ kind: "danger", message: "⚠ " + msg });
    return false;
  }
}

// VAT convention — for the CEO's awareness, NOT for transformation:
//   • POS "rev" column = INCLUDES 15% VAT (what customer pays)
//   • Cost file uploaded by CEO = EXCLUDES VAT
// The CEO explicitly asked to keep the numbers AS-IS in the simulator —
// he wants the customer-facing price (with VAT) shown directly so what he
// sees here matches what's on the menu/receipt. The margin math still
// works because the SAME bias appears on both sides during day-to-day
// comparisons. Only the backend `_margin_pct` was fixed for the *separate*
// `cost * qty` multiplication bug.

// ⚠ ROOT-CAUSE PRICE BUG (v119.51 fix):
//   `rev` is NET revenue AFTER applying promotional discounts. For heavily
//   discounted SKUs (e.g. "قهوة 59" with 218k SAR in disc against 224k gross,
//   ~97% promo), `rev/qty` collapsed to ~0.29 ر — meaningless for simulating
//   NEW discount %. The customer-facing menu price is `gross_with_tax/qty`
//   (what shows on receipt BEFORE the promo coupon is applied). We now
//   prefer that field and fall back to `rev` only when older exports
//   predate the column.
//
//  v119.52 follow-up — Thawany menu prices are always WHOLE riyals (12 ر,
//  18 ر, 22 ر …). The tiny decimal noise we were producing (e.g. 12.07,
//  22.12) came from rounding inside POS exports + a handful of refunds
//  reducing gross by a fraction. Snap the displayed unit price to the
//  nearest integer riyal so it matches the actual menu sticker.
function pickUnitPrice(p) {
  const qty = Number(p.qty) || 0;
  if (qty <= 0) return 0;
  const gross = Number(p.gross_with_tax) || 0;
  const raw = (gross > 0 ? gross : (Number(p.rev) || 0)) / qty;
  return Math.round(raw);
}

// Shared product-from-system list builder. Picks unit price from sales report
// and unit cost — preference order:
//   1) Per-product `cost` field from prod file ⇒ unit = cost/qty
//   2) Standalone cost file (matched by name) ⇒ unit cost directly
//   3) Derived from (rev - profit) / qty as a last resort.
function buildItems(prods, costs) {
  const costMap = new Map((costs || []).map((c) => [(c.name || "").toLowerCase().trim(), Number(c.cost)]));
  return (prods || [])
    .filter((p) => p.qty > 0 && p.rev > 0)
    .map((p) => {
      const unitPrice = pickUnitPrice(p);
      // v205 — نميّز «لا توجد تكلفة مرفوعة» عن «تكلفة = صفر مصرَّحة».
      // تكلفة مرفوعة = حقل cost في ملف المنتجات، أو مطابقة في ملف التكاليف.
      // الاشتقاق من (rev-profit) ليس تكلفة مرفوعة ⇒ يُعتبر غياباً.
      const nameKey = (p.name || "").toLowerCase().trim();
      const prodCostHas = p.cost !== null && p.cost !== undefined && p.cost !== ""
        && Number.isFinite(Number(p.cost));
      const costFileHas = costMap.has(nameKey) && Number.isFinite(costMap.get(nameKey));
      let unitCost = null;
      let hasCost = false;
      if (prodCostHas) { unitCost = Number(p.cost) / (Number(p.qty) || 1); hasCost = true; }
      else if (costFileHas) { unitCost = Number(costMap.get(nameKey)); hasCost = true; }
      return { name: p.name, qty: p.qty, unitPrice, unitCost, hasCost };
    })
    .sort((a, b) => b.qty - a.qty);
}

// ─── Page shell ──────────────────────────────────────────────────────────
export default function Simulator({ fil, prods, costs }) {
  const [mode, setMode] = useState("offer"); // "offer" | "combo"
  const items = useMemo(() => buildItems(prods, costs), [prods, costs]);
  const [vatRate, setVatRate] = useState(DEFAULT_VAT_RATE);

  useEffect(() => {
    api.get("/pos/settings")
      .then((r) => {
        const v = Number(r.data?.vat_rate);
        if (Number.isFinite(v) && v >= 0 && v < 1) setVatRate(v);
      })
      .catch(() => { /* keep default */ });
  }, []);

  const vatPct = Math.round(vatRate * 1000) / 10;

  return (
    <div data-testid="simulator-tab">
      <ApprovalsInbox />
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
          🧪 <span>محاكي العروض والكومبو</span>
        </h3>
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.7 }}>
          أداتان جاهزتان لفريق التسويق: <b>محاكي العروض</b> يحسب السعر بعد الخصم والعدد المطلوب لتحقيق إيراد مستهدف،
          و<b>محاكي الكومبو</b> يجمع منتجين ويُخرج التكلفة وسعر البيع المقترح والربح المتوقّع.
          الأسعار والتكاليف تُجلب تلقائياً من المنتجات المرفوعة في النظام.
        </p>
        <div data-testid="sim-vat-note" style={{
          marginTop: 8, padding: "8px 12px", background: "#fffdf7",
          borderInlineStart: "3px solid #d6a771", borderRadius: 6,
          fontSize: 11.5, color: "var(--brown-deep)", lineHeight: 1.7,
        }}>
          🛈 <b>الهامش محسوب على السعر بلا ضريبة</b>: أسعار POS شاملة ضريبة {vatPct}%، والتكلفة بلا ضريبة. نستبعد الضريبة من سعر البيع (الشامل ÷ {(1 + vatRate).toFixed(2)}) قبل طرح التكلفة، فيكون الربح والهامش على أساس صافٍ. نسبة الضريبة من إعدادات النظام.
        </div>
        <div className="sim-mode" data-testid="sim-mode">
          <button
            type="button"
            className={`sim-mode-btn ${mode === "offer" ? "is-on" : ""}`}
            onClick={() => setMode("offer")}
            data-testid="sim-mode-offer"
          >🎟 محاكي العروض</button>
          <button
            type="button"
            className={`sim-mode-btn ${mode === "combo" ? "is-on" : ""}`}
            onClick={() => setMode("combo")}
            data-testid="sim-mode-combo"
          >🍰 محاكي الكومبو</button>
        </div>
      </div>

      {mode === "offer" && <OfferSimulator fil={fil} items={items} vatRate={vatRate} />}
      {mode === "combo" && <ComboSimulator items={items} vatRate={vatRate} />}
    </div>
  );
}

// ─── Offer Simulator ─────────────────────────────────────────────────────
function OfferSimulator({ fil, items, vatRate = DEFAULT_VAT_RATE }) {
  // Fallback baseline if no products uploaded yet — keeps the simulator usable.
  const fallbackPrice = useMemo(() => {
    if (!fil?.length) return 30;
    const paid = fil.filter((r) => r.isPaid);
    if (!paid.length) return 30;
    const t = paid.reduce((s, r) => s + (r.total || 0), 0) / paid.length;
    return Math.max(5, Math.round(t));
  }, [fil]);

  const [productName, setProductName] = useState("");
  const [discountPct, setDiscountPct] = useState(20);
  const [duration, setDuration] = useState(14);

  React.useEffect(() => {
    if (!productName && items[0]) setProductName(items[0].name);
  }, [items, productName]);

  const product = items.find((x) => x.name === productName);
  const originalPrice = product ? product.unitPrice : fallbackPrice;
  const unitCost = product ? product.unitCost : 0;
  // v204 — الحساب الصحيح: استبعاد الضريبة من السعر قبل طرح التكلفة.
  const calc = offerMath({ priceIncVat: originalPrice, unitCost, discountPct, vatRate });
  const priceAfter = calc.priceAfterIncVat;         // شامل الضريبة (للعرض على العميل)
  const priceAfterExVat = calc.priceAfterExVat;     // بلا ضريبة (أساس الهامش)
  const priceExVat = calc.priceExVat;               // السعر الأصلي بلا ضريبة
  const discountAmount = calc.discountAmountIncVat;
  const profitPerUnitAfter = calc.profit;
  const marginAfterPct = calc.marginPct;
  const missingCost = !!product && !product.hasCost;

  const presetDiscounts = [10, 15, 20, 25, 30, 50];
  const offerHealthy = calc.healthy;

  const exportPlan = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCSV(`خطة_عرض_${(productName || "عام").replace(/\s+/g, "_")}_${today}.csv`,
      ["البيان", "القيمة"],
      [
        ["المنتج", productName || "عام"],
        ["نسبة الخصم", `${discountPct}%`],
        ["مدة العرض", `${duration} يوم`],
        ["السعر الأصلي (شامل الضريبة)", `${fmt(originalPrice)} ر`],
        ["السعر بعد الخصم (شامل الضريبة)", `${fmt(priceAfter)} ر`],
        ["السعر بعد الخصم (بلا ضريبة)", `${fmt(priceAfterExVat)} ر`],
        ["التكلفة (بلا ضريبة)", `${fmt(unitCost)} ر`],
        ["الربح/وحدة (على أساس بلا ضريبة)", `${fmt(profitPerUnitAfter)} ر`],
        ["الهامش (على السعر بلا ضريبة)", `${fmt(marginAfterPct, 1)}%`],
        ["إجمالي الخصم على كل وحدة (شامل)", `${fmt(discountAmount)} ر`],
      ]
    );
  };

  return (
    <div data-testid="offer-simulator">
      {/* Inputs */}
      <div className="card sim-card" style={{ marginBottom: 14 }}>
        <div className="sim-grid-2">
          <div className="sim-field" style={{ gridColumn: items.length > 0 ? "span 2" : "auto" }}>
            <label className="sim-label">المنتج (السعر والتكلفة يُجلبان من النظام)</label>
            {items.length > 0 ? (
              <select
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="sim-input"
                data-testid="off-product"
              >
                {items.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name} · سعر {p.unitPrice} ر · {p.hasCost ? `تكلفة ${p.unitCost.toFixed(2)} ر` : "⚠ بلا تكلفة مرفوعة"}
                  </option>
                ))}
              </select>
            ) : (
              <div className="sim-hint" style={{ padding: "10px 12px", background: "var(--beige)", borderRadius: 8 }}>
                ⚠ لا توجد منتجات مرفوعة — يُستخدم متوسط الفاتورة ({fmt(fallbackPrice, 0)} ر) كافتراضي. ارفع تقرير المنتجات لاختيار منتج محدّد.
              </div>
            )}
          </div>

          <div className="sim-field">
            <label className="sim-label">نسبة الخصم</label>
            <div className="sim-presets" data-testid="off-presets">
              {presetDiscounts.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`sim-chip ${discountPct === p ? "is-on" : ""}`}
                  onClick={() => setDiscountPct(p)}
                  data-testid={`off-preset-${p}`}
                >{p}%</button>
              ))}
            </div>
            <input
              type="range" min="5" max="70" step="1"
              value={discountPct}
              onChange={(e) => setDiscountPct(+e.target.value)}
              data-testid="off-discount-range"
              className="sim-range"
            />
          </div>

          <div className="sim-field">
            <label className="sim-label">مدة العرض (بالأيام)</label>
            <div className="sim-input-with-suffix">
              <input
                type="number" min="1" max="180"
                value={duration}
                onChange={(e) => setDuration(Math.max(1, +e.target.value || 1))}
                data-testid="off-duration-input"
                className="sim-input"
              />
              <span className="sim-suffix">يوم</span>
            </div>
            <input
              type="range" min="1" max="60" step="1"
              value={duration}
              onChange={(e) => setDuration(+e.target.value)}
              className="sim-range"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card sim-result-card" data-testid="off-results">
        <div className="sim-result-head">
          <span className="sim-result-title">📋 خطة العرض</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="btn btn-soft" onClick={exportPlan} data-testid="off-export-btn" style={{ fontSize: 12 }}>
              📥 CSV
            </button>
            <button type="button" className="btn btn-success" data-testid="off-submit-approval"
              disabled={!product || missingCost}
              onClick={() => submitPlanForApproval({
                plan_type: "offer",
                title: `عرض ${product?.name || ""} — ${discountPct}%`,
                summary: `خصم ${discountPct}% على ${product?.name || ""} · سعر بعد العرض ${fmt(priceAfter)} ر · مدة ${duration} يوم`,
                cost_sar: 0,
                payload: { product: product?.name, discountPct, duration, vatRate,
                  priceIncVat: originalPrice, priceAfterIncVat: priceAfter,
                  priceAfterExVat, unitCostExVat: unitCost,
                  profitPerUnitAfter, marginAfterPct, basis: "ex_vat" },
              })}
              style={{ fontSize: 12 }}>
              📤 رفع للاعتماد
            </button>
          </div>
        </div>

        <div className="sim-price-hero" data-testid="off-price-hero">
          <div className="sim-price-block">
            <span className="sim-price-l">السعر قبل العرض</span>
            <span className="sim-price-v sim-price-strike">{fmt(originalPrice)} ر</span>
            {product && (missingCost
              ? <span className="sim-hint" style={{ color: "#b91c1c", fontWeight: 800 }}>⚠ لا توجد تكلفة مرفوعة</span>
              : <span className="sim-hint">تكلفة: {fmt(unitCost)} ر</span>)}
          </div>
          <div className="sim-price-arrow">←</div>
          <div className="sim-price-block sim-price-after">
            <span className="sim-price-l">السعر بعد العرض</span>
            <span className="sim-price-v">{fmt(priceAfter)} ر</span>
            {missingCost ? (
              <span className="sim-price-save sim-price-save-bad" data-testid="off-missing-cost">
                لا توجد تكلفة مرفوعة لهذا المنتج — ارفع التكلفة أولًا
              </span>
            ) : (
              <span className={`sim-price-save ${offerHealthy ? "" : "sim-price-save-bad"}`}>
                {offerHealthy
                  ? <>ربح {fmt(profitPerUnitAfter)} ر · هامش {fmt(marginAfterPct, 1)}%</>
                  : <>⚠ خاسر — التكلفة أعلى من السعر بعد الخصم</>}
              </span>
            )}
          </div>
        </div>

        {missingCost ? (
          <div data-testid="off-missing-cost-banner" style={{
            margin: "12px 0", padding: "12px 14px", borderRadius: 10, background: "#fef2f2",
            border: "1.5px solid #dc2626", color: "#7f1d1d", fontSize: 13, fontWeight: 800, lineHeight: 1.8 }}>
            🚫 لا توجد تكلفة مرفوعة لهذا المنتج — ارفع التكلفة أولًا. لا يُعرض ربح ولا هامش، وزر «رفع للاعتماد» معطّل.
          </div>
        ) : (
        <div className="sim-vat-breakdown" data-testid="off-vat-breakdown" style={{
          margin: "12px 0", border: "1px solid #e6d9b8", borderRadius: 10, overflow: "hidden", fontSize: 12.5 }}>
          {[
            ["السعر شامل الضريبة", `${fmt(priceAfter)} ر`, "بعد الخصم · شامل ضريبة"],
            ["السعر بلا ضريبة", `${fmt(priceAfterExVat)} ر`, `الشامل ÷ ${(1 + vatRate).toFixed(2)}`],
            ["التكلفة (بلا ضريبة)", `${fmt(unitCost)} ر`, "من ملف التكاليف"],
            ["الربح (بلا ضريبة)", `${fmt(profitPerUnitAfter)} ر`, "السعر بلا ضريبة − التكلفة"],
            ["الهامش", `${fmt(marginAfterPct, 1)}%`, "على السعر بلا ضريبة"],
          ].map(([label, val, unit], i) => (
            <div key={i} data-testid={`off-vat-row-${i}`} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "7px 12px", background: i % 2 ? "#faf6ec" : "#fff" }}>
              <span style={{ color: "var(--brown-deep)", fontWeight: 700 }}>{label}</span>
              <span style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <b style={{ color: "#5c3a1e" }}>{val}</b>
                <small style={{ color: "#8a6d4d" }}>{unit}</small>
              </span>
            </div>
          ))}
        </div>
        )}

        <div className="sim-summary" data-testid="off-summary">
          <div className="sim-summary-icon">💡</div>
          <div>
            {missingCost ? (
              <>لهذا المنتج <b>لا توجد تكلفة مرفوعة</b> — لا يمكن حساب الربح أو الهامش. ارفع التكلفة في النظام أولًا.</>
            ) : (<>
              بخصم <b>{discountPct}%</b> على <b>{productName || "المنتج"}</b> (سعر {fmt(originalPrice)} ر)،
              السعر الجديد <b>{fmt(priceAfter)} ر</b> لمدة <b>{duration} يوم</b>
              {offerHealthy
                ? <> · الربح في كل وحدة <b>{fmt(profitPerUnitAfter)} ر</b> (هامش {fmt(marginAfterPct, 1)}%).</>
                : <> ⚠ <b>تنبيه:</b> السعر بعد الخصم أقل من التكلفة.</>}
            </>)}
          </div>
        </div>
      </div>
      {/* AI Advisor recommendation panel */}
      <OfferComboAdvisor plan_type="offer"
        items={product ? [{ name: product.name, unitPrice: originalPrice, unitCost, qty: product.qty }] : []}
        discount_pct={discountPct}
        duration_days={duration}
        suggested_price={priceAfter}
        target_margin_pct={marginAfterPct} />
    </div>
  );
}

// ─── Combo Simulator ─────────────────────────────────────────────────────
function ComboSimulator({ items, vatRate = DEFAULT_VAT_RATE }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [p3, setP3] = useState("");
  const [marginPct, setMarginPct] = useState(50);
  const [duration, setDuration] = useState(14);  // NEW: combo offer duration in days

  // AI suggestions state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCombos, setAiCombos] = useState(null);
  const [aiError, setAiError] = useState("");
  const [activatedIdx, setActivatedIdx] = useState(new Set());
  const [activatingIdx, setActivatingIdx] = useState(null);

  React.useEffect(() => {
    if (!p1 && items[0]) setP1(items[0].name);
    if (!p2 && items[1]) setP2(items[1].name);
  }, [items, p1, p2]);

  const find = (name) => items.find((x) => x.name === name);
  const selected = [find(p1), find(p2), find(p3)].filter(Boolean);

  // v204 — منطق منفصل عن محاكي العروض: السعر المقترح مشتق من التكلفة
  // وهامش مستهدف (أساس بلا ضريبة). نعرض المكافئ الشامل للعميل ونصالح
  // التوفير على أساس شامل مقابل شامل.
  const cmb = comboMath({ items: selected, targetMarginPct: marginPct, vatRate });
  const sumPrice = cmb.sumPriceIncVat;          // شامل الضريبة (أسعار القائمة)
  const sumCost = cmb.sumCost;                  // بلا ضريبة
  const suggested = cmb.suggestedExVat;         // سعر البيع المقترح بلا ضريبة
  const suggestedIncVat = cmb.suggestedIncVat;  // المكافئ الشامل للعميل
  const profitPerCombo = cmb.profit;
  const realMarginPct = cmb.realMarginPct;
  const savingsVsSeparate = cmb.savingsVsSeparateIncVat;
  const comboMissingCost = selected.length > 0 && !cmb.hasCost;
  const missingNames = selected.filter((s) => !s.hasCost).map((s) => s.name);

  const fetchAiCombos = async () => {
    setAiLoading(true);
    setAiError("");
    setActivatedIdx(new Set());
    try {
      const { data } = await api.post("/ai/combo-suggestions");
      setAiCombos(data.combos || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "فشل اقتراح الكومبو من AI";
      setAiError(typeof msg === "string" ? msg : "خطأ غير متوقع");
    } finally {
      setAiLoading(false);
    }
  };

  const activateAsCampaign = async (idx, combo) => {
    setActivatingIdx(idx);
    setAiError("");
    try {
      const today = ksaToday();
      const endStr = ksaAddDays(today, 30);
      const desc = (
        `كومبو AI: ${combo.item_1} + ${combo.item_2}\n` +
        `سعر الكومبو: ${combo.suggested_price} ر · تكلفة: ${combo.total_cost} ر\n` +
        `ربح متوقّع/كومبو: ${combo.expected_profit} ر (${combo.expected_margin_pct}%)\n` +
        `العميل يوفّر: ${combo.customer_saves} ر مقابل الشراء منفصلاً\n` +
        `لماذا: ${combo.why || ""}\n` +
        (combo.best_for ? `الأنسب لـ: ${combo.best_for}` : "")
      );
      await api.post("/campaigns", {
        name: `🍰 ${combo.name}`,
        type: "combo",
        start_date: today,
        end_date: endStr,
        description: desc,
        targeted_phones: [],
        cost_sar: 0,
        notes: `AI-generated combo · سعر ${combo.suggested_price} ر · هامش ${combo.expected_margin_pct}%`,
      });
      const next = new Set(activatedIdx);
      next.add(idx);
      setActivatedIdx(next);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "فشل تفعيل الحملة";
      setAiError(typeof msg === "string" ? msg : "خطأ غير متوقع");
    } finally {
      setActivatingIdx(null);
    }
  };

  const applyCombo = (combo) => {
    // Best-effort match against our local items list (case-insensitive)
    const match = (n) => items.find((it) => it.name.trim().toLowerCase() === String(n || "").trim().toLowerCase());
    const i1 = match(combo.item_1)?.name;
    const i2 = match(combo.item_2)?.name;
    if (i1) setP1(i1);
    if (i2) setP2(i2);
    setP3("");
    if (combo.expected_margin_pct) setMarginPct(Math.round(combo.expected_margin_pct));
    // Scroll to top of combo card
    setTimeout(() => {
      const el = document.querySelector('[data-testid="combo-results"]');
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const exportCombo = () => {
    const today = new Date().toISOString().slice(0, 10);
    const cmbNames = selected.map((s) => s.name).join(" + ");
    downloadCSV(`خطة_كومبو_${today}.csv`,
      ["البيان", "القيمة"],
      [
        ["المنتجات", cmbNames],
        ["مجموع تكلفة المواد (بلا ضريبة)", `${fmt(sumCost)} ر`],
        ["مجموع السعر منفصلاً (شامل الضريبة)", `${fmt(sumPrice)} ر`],
        ["نسبة الربح المستهدفة", `${marginPct}%`],
        ["السعر المقترح للكومبو (بلا ضريبة)", `${fmt(suggested)} ر`],
        ["السعر المقترح للكومبو (شامل الضريبة — للعميل)", `${fmt(suggestedIncVat)} ر`],
        ["الربح لكل كومبو (بلا ضريبة)", `${fmt(profitPerCombo)} ر`],
        ["نسبة الربح الفعلية (على السعر بلا ضريبة)", `${fmt(realMarginPct, 1)}%`],
        ["توفير العميل مقابل الشراء منفصلاً (شامل مقابل شامل)", `${fmt(savingsVsSeparate)} ر`],
      ]
    );
  };

  if (!items.length) {
    return (
      <div className="card" style={{ padding: 24, textAlign: "center" }} data-testid="combo-empty">
        <div style={{ fontSize: 32, marginBottom: 6 }}>📦</div>
        <div style={{ fontSize: 13, color: "var(--brown)", fontWeight: 600 }}>محاكي الكومبو</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
          ارفع تقرير المنتجات أولاً ليظهر المحاكي.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="combo-simulator">
      {/* AI suggestions panel */}
      <div className="card ai-combos-panel" data-testid="ai-combos-panel">
        <div className="ai-combos-head">
          <div>
            <div className="ai-combos-title">🤖 كومبو مقترح من AI</div>
            <div className="ai-combos-sub">يحلل بياناتك الفعلية (مبيعات + تكاليف) ويقترح أفضل 5 توليفات مربحة</div>
          </div>
          <button
            type="button"
            className="btn btn-success ai-combos-btn"
            onClick={fetchAiCombos}
            disabled={aiLoading}
            data-testid="ai-combos-fetch"
          >
            {aiLoading ? "⏳ جارٍ التحليل..." : aiCombos ? "🔄 توليفات جديدة" : "✨ اقترح لي"}
          </button>
        </div>
        {aiError && (
          <div className="ai-combos-error" data-testid="ai-combos-error">⚠ {aiError}</div>
        )}
        {aiCombos && aiCombos.length > 0 && (
          <div className="ai-combos-grid" data-testid="ai-combos-list">
            {aiCombos.map((c, i) => (
              <div key={i} className="ai-combo-card" data-testid={`ai-combo-card-${i}`}>
                <div className="ai-combo-name">{c.name}</div>
                <div className="ai-combo-items">
                  <span>🧋 {c.item_1}</span>
                  <span className="ai-combo-plus">+</span>
                  <span>🍰 {c.item_2}</span>
                </div>
                {(c.unit_cost_1 != null || c.unit_cost_2 != null) && (
                  <div className="ai-combo-row" data-testid={`ai-combo-cost-breakdown-${i}`}
                    style={{ fontSize: 10.5, color: "var(--muted)", padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
                    <span>{c.item_1}: تكلفة {fmt(c.unit_cost_1)} · سعر {fmt(c.unit_price_1)}</span>
                  </div>
                )}
                {(c.unit_cost_2 != null) && (
                  <div className="ai-combo-row"
                    style={{ fontSize: 10.5, color: "var(--muted)", padding: "4px 0", borderBottom: "1px dashed var(--border)" }}>
                    <span>{c.item_2}: تكلفة {fmt(c.unit_cost_2)} · سعر {fmt(c.unit_price_2)}</span>
                  </div>
                )}
                <div className="ai-combo-pricing">
                  <div className="ai-combo-row">
                    <span>التكلفة</span><b>{fmt(c.total_cost)} ر</b>
                  </div>
                  <div className="ai-combo-row">
                    <span>سعر مفرد</span><b style={{ textDecoration: "line-through", color: "var(--muted)" }}>{fmt(c.separate_price)} ر</b>
                  </div>
                  <div className="ai-combo-row ai-combo-row-hl">
                    <span>سعر الكومبو</span><b style={{ color: "var(--success)" }}>{fmt(c.suggested_price)} ر</b>
                  </div>
                  <div className="ai-combo-row">
                    <span>الربح / الهامش</span>
                    <b>{fmt(c.expected_profit)} ر · {fmt(c.expected_margin_pct, 0)}%</b>
                  </div>
                  {c.customer_saves != null && (
                    <div className="ai-combo-row">
                      <span>توفير العميل</span><b style={{ color: "var(--gold)" }}>{fmt(c.customer_saves)} ر</b>
                    </div>
                  )}
                </div>
                <div className="ai-combo-why">
                  <b>لماذا:</b> {c.why}
                </div>
                {c.best_for && (
                  <div className="ai-combo-best">🎯 <b>الأنسب لـ:</b> {c.best_for}</div>
                )}
                <div className="ai-combo-actions">
                  <button
                    type="button"
                    className="btn btn-outline ai-combo-apply"
                    onClick={() => applyCombo(c)}
                    data-testid={`ai-combo-apply-${i}`}
                  >📥 طبّق</button>
                  <button
                    type="button"
                    className={`btn ${activatedIdx.has(i) ? "btn-success" : "btn-primary"} ai-combo-activate`}
                    onClick={() => activateAsCampaign(i, c)}
                    disabled={activatedIdx.has(i) || activatingIdx === i}
                    data-testid={`ai-combo-activate-${i}`}
                  >
                    {activatedIdx.has(i) ? "✓ مفعّل كحملة"
                      : activatingIdx === i ? "⏳ جارٍ التفعيل…"
                      : "🚀 فعّل كحملة"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card sim-card" style={{ marginBottom: 14 }}>
        <div className="sim-grid-2">
          <ProductPicker label="المنتج الأول" value={p1} setValue={setP1} items={items} testid="combo-p1" />
          <ProductPicker label="المنتج الثاني" value={p2} setValue={setP2} items={items} testid="combo-p2" />
          <ProductPicker label="منتج ثالث (اختياري)" value={p3} setValue={setP3} items={items} testid="combo-p3" allowEmpty />

          <div className="sim-field">
            <label className="sim-label">نسبة الربح المستهدفة</label>
            <div className="sim-presets">
              {[30, 40, 50, 60, 70].map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`sim-chip ${marginPct === m ? "is-on" : ""}`}
                  onClick={() => setMarginPct(m)}
                  data-testid={`combo-margin-${m}`}
                >{m}%</button>
              ))}
            </div>
            <input
              type="range" min="10" max="80" step="1"
              value={marginPct}
              onChange={(e) => setMarginPct(+e.target.value)}
              className="sim-range"
              data-testid="combo-margin-range"
            />
          </div>
          <div className="sim-field">
            <label className="sim-label">مدة العرض (بالأيام)</label>
            <div className="sim-input-with-suffix">
              <input
                type="number" min="1" max="180"
                value={duration}
                onChange={(e) => setDuration(Math.max(1, +e.target.value || 1))}
                data-testid="combo-duration-input"
                className="sim-input"
              />
              <span className="sim-suffix">يوم</span>
            </div>
            <input
              type="range" min="1" max="60" step="1"
              value={duration}
              onChange={(e) => setDuration(+e.target.value)}
              className="sim-range"
            />
          </div>
        </div>
      </div>

      <div className="card sim-result-card" data-testid="combo-results">
        <div className="sim-result-head">
          <span className="sim-result-title">🍰 خطة الكومبو</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="btn btn-soft" onClick={exportCombo} data-testid="combo-export-btn" style={{ fontSize: 12 }}>
              📥 CSV
            </button>
            <button type="button" className="btn btn-success" data-testid="combo-submit-approval"
              disabled={selected.length < 2 || comboMissingCost}
              onClick={() => submitPlanForApproval({
                plan_type: "combo",
                title: `كومبو: ${selected.map((s) => s.name).join(" + ")}`,
                summary: `${selected.length} منتجات · سعر مقترح ${fmt(suggested)} ر · ربح ${fmt(profitPerCombo)} ر (${fmt(realMarginPct, 1)}%)`,
                cost_sar: 0,
                payload: {
                  items: selected.map((s) => ({ name: s.name, unitCost: s.unitCost, unitPrice: s.unitPrice })),
                  marginPct, vatRate, suggestedExVat: suggested, suggestedIncVat,
                  sumCostExVat: sumCost, sumPriceIncVat: sumPrice,
                  profitPerCombo, realMarginPct, savingsVsSeparateIncVat: savingsVsSeparate,
                  basis: "ex_vat",
                },
              })}
              style={{ fontSize: 12 }}>
              📤 رفع للاعتماد
            </button>
          </div>
        </div>

        <div className="combo-list" data-testid="combo-breakdown">
          {selected.map((s, i) => (
            <div key={s.name + i} className="combo-row" data-testid={`combo-item-${i}`}>
              <span className="combo-row-name">{s.name}</span>
              <span className="combo-row-meta">
                {s.hasCost
                  ? <>تكلفة <b>{fmt(s.unitCost)} ر</b> · سعر مفرد <b>{fmt(s.unitPrice)} ر</b></>
                  : <><b style={{ color: "#b91c1c" }}>⚠ بلا تكلفة مرفوعة</b> · سعر مفرد <b>{fmt(s.unitPrice)} ر</b></>}
              </span>
            </div>
          ))}
          <div className="combo-row combo-row-total">
            <span className="combo-row-name">المجموع</span>
            <span className="combo-row-meta">
              {comboMissingCost
                ? <b style={{ color: "#b91c1c" }}>التكلفة غير مكتملة</b>
                : <>تكلفة <b>{fmt(sumCost)} ر</b> · سعر مفرد <b>{fmt(sumPrice)} ر</b></>}
            </span>
          </div>
        </div>

        {comboMissingCost ? (
          <div data-testid="combo-missing-cost-banner" style={{
            margin: "12px 0", padding: "12px 14px", borderRadius: 10, background: "#fef2f2",
            border: "1.5px solid #dc2626", color: "#7f1d1d", fontSize: 13, fontWeight: 800, lineHeight: 1.8 }}>
            🚫 لا توجد تكلفة مرفوعة لـ: <b>{missingNames.join("، ")}</b> — ارفع التكلفة أولًا. لا يُعرض سعر مقترح ولا ربح ولا هامش، وزر «رفع للاعتماد» معطّل.
          </div>
        ) : (<>
        <div className="sim-price-hero">
          <div className="sim-price-block">
            <span className="sim-price-l">تكلفة المواد (بلا ضريبة)</span>
            <span className="sim-price-v" style={{ color: "var(--danger)" }}>{fmt(sumCost)} ر</span>
          </div>
          <div className="sim-price-arrow">←</div>
          <div className="sim-price-block sim-price-after">
            <span className="sim-price-l">السعر المقترح (بلا ضريبة)</span>
            <span className="sim-price-v" data-testid="combo-suggested-exvat">{fmt(suggested)} ر</span>
            <span className="sim-hint">للعميل شامل الضريبة: <b data-testid="combo-suggested-incvat">{fmt(suggestedIncVat)} ر</b></span>
            <span className="sim-price-save">ربح {fmt(profitPerCombo)} ر · هامش {fmt(realMarginPct, 1)}% (على السعر بلا ضريبة)</span>
          </div>
        </div>

        <div className="sim-targets">
          <div className="sim-target-card" data-testid="combo-customer-saves">
            <div className="sim-target-l">العميل يوفّر</div>
            <div className="sim-target-v">{fmt(savingsVsSeparate)} ر</div>
            <div className="sim-target-s">شامل مقابل شامل</div>
          </div>
          <div className="sim-target-card" data-testid="combo-profit-per">
            <div className="sim-target-l">الربح لكل كومبو</div>
            <div className="sim-target-v" style={{ color: "var(--success)" }}>{fmt(profitPerCombo)} ر</div>
            <div className="sim-target-s">بلا ضريبة (سعر − تكلفة)</div>
          </div>
          <div className="sim-target-card" data-testid="combo-margin-real">
            <div className="sim-target-l">نسبة الربح الفعلية</div>
            <div className="sim-target-v">{fmt(realMarginPct, 1)}%</div>
            <div className="sim-target-s">على السعر بلا ضريبة</div>
          </div>
        </div>

        <div className="sim-summary">
          <div className="sim-summary-icon">💡</div>
          <div>
            كومبو <b>&quot;{selected.map((s) => s.name).join(" + ")}&quot;</b> بتكلفة <b>{fmt(sumCost)} ر</b> (بلا ضريبة)،
            بِع بسعر <b>{fmt(suggested)} ر</b> بلا ضريبة (<b>{fmt(suggestedIncVat)} ر</b> شامل الضريبة للعميل)
            لتحقيق ربح <b>{fmt(profitPerCombo)} ر</b> ({fmt(realMarginPct, 1)}%) لكل كومبو.
            العميل يوفّر <b>{fmt(savingsVsSeparate)} ر</b> مقابل الشراء منفصلاً.
          </div>
        </div>
        </>)}
      </div>
      <OfferComboAdvisor plan_type="combo"
        items={selected.map((s) => ({ name: s.name, unitPrice: s.unitPrice, unitCost: s.unitCost, qty: s.qty }))}
        discount_pct={savingsVsSeparate > 0 && sumPrice > 0 ? Math.round((savingsVsSeparate / sumPrice) * 100) : 0}
        duration_days={duration}
        suggested_price={suggested}
        target_margin_pct={realMarginPct} />
    </div>
  );
}

function ProductPicker({ label, value, setValue, items, testid, allowEmpty }) {
  return (
    <div className="sim-field">
      <label className="sim-label">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => setValue(e.target.value)}
        className="sim-input"
        data-testid={testid}
      >
        {allowEmpty && <option value="">— بدون —</option>}
        {items.map((p) => (
          <option key={p.name} value={p.name}>
            {p.name} · سعر {p.unitPrice} ر · {p.hasCost ? `تكلفة ${p.unitCost.toFixed(2)} ر` : "⚠ بلا تكلفة مرفوعة"}
          </option>
        ))}
      </select>
    </div>
  );
}


// ─── Approvals inbox ────────────────────────────────────────────────────
// Top-of-page card that lists plans waiting on the current user's role:
//   role=operations → plans waiting for ops sign-off (pending_ops)
//   role=admin       → plans waiting for CEO sign-off (pending_ceo)
//   anyone else      → their own submitted plans (read-only history)
// The card stays hidden when there's nothing to act on, so the simulator
// view isn't cluttered for marketing users with no inbox.
function ApprovalsInbox() {
  const [items, setItems] = React.useState([]);
  const [role, setRole] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const reload = React.useCallback(async () => {
    try {
      // Cheap dual-fetch: my own submissions + items waiting on my role.
      const [me, mine] = await Promise.all([
        api.get("/me").catch(() => ({ data: { user: { role: "" } } })),
        api.get("/combo-approvals", { params: { mine: true } }),
      ]);
      const r = (me.data?.user?.role || me.data?.role || "").toLowerCase();
      setRole(r);
      setItems(mine.data?.items || []);
    } catch (e) { /* keep silent — non-critical */ }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const act = async (id, kind) => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === "approve-ops") {
        await api.post(`/combo-approvals/${id}/approve-ops`, { note: "" });
        showToast({ kind: "success", message: "✅ اعتُمدت — انتقلت لاعتماد المدير التنفيذي" });
      } else if (kind === "approve-ceo") {
        await api.post(`/combo-approvals/${id}/approve-ceo`, { note: "" });
        showToast({ kind: "success", message: "🚀 اعتُمدت ونُشرت في الحملات" });
      } else if (kind === "reject") {
        const reason = window.prompt("سبب الرفض؟");
        if (!reason) { setBusy(false); return; }
        await api.post(`/combo-approvals/${id}/reject`, { reason });
        showToast({ kind: "warn", message: "🚫 تم الرفض" });
      }
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      showToast({ kind: "danger", message: typeof d === "string" ? d : (e?.message || "فشل") });
    } finally { setBusy(false); }
  };

  if (loading) return null;
  if (!items.length) return null;

  const isApprover = role === "operations" || role === "admin";
  const heading = role === "operations"
    ? "📋 خطط بانتظار اعتمادك (مدير التشغيل)"
    : role === "admin"
      ? "🏛️ خطط بانتظار اعتمادك (المدير التنفيذي)"
      : "📤 خططك المرفوعة";

  return (
    <div className="card" data-testid="approvals-inbox"
      style={{ marginBottom: 14, padding: 14, borderInlineStart: "4px solid #b8860b" }}>
      <div style={{ fontWeight: 800, color: "var(--brown-deep)", marginBottom: 8 }}>{heading} ({items.length})</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((a) => (
          <div key={a.id} data-testid={`approval-${a.id}`}
            style={{
              padding: 10, borderRadius: 8, border: "1px solid var(--border)",
              background: "#fffdf7",
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontWeight: 700, color: "var(--brown-deep)", fontSize: 13.5 }}>
                {a.plan_type === "offer" ? "🎟" : "🍰"} {a.title}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3 }}>
                👤 {a.submitter_name} · ⏱ {formatKSADate(new Date(a.submitted_at))} ·
                <span style={{
                  padding: "1px 8px", borderRadius: 999, marginInlineStart: 6, fontSize: 10.5, fontWeight: 700,
                  background: a.status === "pending_ops" ? "#fef3c7" : a.status === "pending_ceo" ? "#dbeafe" : a.status === "approved" ? "#dcfce7" : "#fee2e2",
                  color: a.status === "pending_ops" ? "#92400e" : a.status === "pending_ceo" ? "#1e40af" : a.status === "approved" ? "#15803d" : "#991b1b",
                }}>
                  {a.status === "pending_ops" ? "بانتظار التشغيل" : a.status === "pending_ceo" ? "بانتظار المدير التنفيذي" : a.status === "approved" ? "معتمدة + منشورة" : "مرفوضة"}
                </span>
              </div>
              {a.summary && <div style={{ fontSize: 12, marginTop: 4, color: "var(--brown-deep)" }}>{a.summary}</div>}
              {a.rejection_reason && <div style={{ fontSize: 11.5, marginTop: 4, color: "#991b1b" }}>❌ {a.rejection_reason}</div>}
            </div>
            {isApprover && a.status === "pending_ops" && role === "operations" && (
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" disabled={busy} onClick={() => act(a.id, "approve-ops")} data-testid={`approval-ops-approve-${a.id}`}
                  className="btn btn-success" style={{ fontSize: 11 }}>✅ اعتماد</button>
                <button type="button" disabled={busy} onClick={() => act(a.id, "reject")} data-testid={`approval-ops-reject-${a.id}`}
                  className="btn" style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }}>رفض</button>
              </div>
            )}
            {isApprover && a.status === "pending_ceo" && role === "admin" && (
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" disabled={busy} onClick={() => act(a.id, "approve-ceo")} data-testid={`approval-ceo-approve-${a.id}`}
                  className="btn btn-success" style={{ fontSize: 11 }}>🚀 اعتماد ونشر</button>
                <button type="button" disabled={busy} onClick={() => act(a.id, "reject")} data-testid={`approval-ceo-reject-${a.id}`}
                  className="btn" style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }}>رفض</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── 🧠 Offer/Combo AI Advisor ──────────────────────────────────────────
// Below-simulator panel. Asks Claude to evaluate the configured offer/combo
// using customer behavior, Saudi calendar, weather, and finance — then
// surfaces a structured verdict (go/wait/no) + insights + final advice.
function OfferComboAdvisor({ plan_type, items, discount_pct, duration_days, suggested_price, target_margin_pct }) {
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState(null);
  const [err, setErr] = useState("");
  const canAsk = (items || []).length > 0 && (duration_days || 0) > 0;

  const askAdvisor = async () => {
    if (!canAsk) { setErr("اختر منتجاً ومدة العرض أولاً"); return; }
    setLoading(true); setErr(""); setAdvice(null);
    try {
      const { data } = await api.post("/marketing/offer-advice", {
        plan_type, items, discount_pct, duration_days, suggested_price, target_margin_pct,
      }, { timeout: 120000 });
      setAdvice(data.advice || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "تعذّر التحليل");
    } finally { setLoading(false); }
  };

  const verdictMeta = {
    go: { label: "نفّذ الآن", bg: "#dcfce7", fg: "#15803d", border: "#16a34a", emoji: "✅" },
    wait: { label: "انتظر / حسّن", bg: "#fef3c7", fg: "#92400e", border: "#d97706", emoji: "⏸" },
    no: { label: "لا تنفّذ", bg: "#fee2e2", fg: "#991b1b", border: "#dc2626", emoji: "❌" },
  };
  const v = advice?.verdict && verdictMeta[advice.verdict] ? verdictMeta[advice.verdict] : null;

  return (
    <div className="card" data-testid="advisor-panel"
      style={{ marginTop: 14, padding: 16, background: "linear-gradient(135deg, #fffdf7 0%, #fff8e6 100%)", borderInlineStart: "4px solid #6b4423" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 800, color: "#6b4423", fontSize: 15 }}>🧠 رأي مستشار العروض والكومبو</div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3 }}>
            تحليل مبني على سلوك العملاء + الطقس + التقويم السعودي + الوضع المالي.
          </div>
        </div>
        <button type="button" onClick={askAdvisor} disabled={loading || !canAsk}
          data-testid="advisor-ask-btn"
          style={{ padding: "10px 18px", borderRadius: 10, background: "#6b4423", color: "#fff8e6", border: "none", fontWeight: 800, cursor: loading || !canAsk ? "not-allowed" : "pointer", fontSize: 13.5, opacity: loading || !canAsk ? 0.6 : 1 }}>
          {loading ? "⏳ يحلّل…" : "🤖 استشر المستشار"}
        </button>
      </div>

      {err && <div data-testid="advisor-error" style={{ marginTop: 10, padding: 10, background: "#fee2e2", color: "#991b1b", borderRadius: 6, fontSize: 12.5 }}>⚠ {err}</div>}

      {loading && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
          ⏳ المستشار يحلّل بياناتك الحية… (~30-60 ثانية)
        </div>
      )}

      {advice && (
        <div data-testid="advisor-content" style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {v && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: v.bg, borderRadius: 10, borderInlineStart: `5px solid ${v.border}` }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: v.fg, whiteSpace: "nowrap" }}>{v.emoji} {v.label}</div>
              {advice.headline && <div style={{ color: v.fg, fontSize: 13.5, fontWeight: 700, lineHeight: 1.5 }}>{advice.headline}</div>}
            </div>
          )}

          {(advice.expected_profit_margin_pct !== undefined || advice.expected_units_per_day !== undefined || advice.expected_total_revenue !== undefined || advice.expected_total_profit !== undefined) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
              {advice.expected_profit_margin_pct !== undefined && (
                <div className="card" style={{ padding: 10, background: "#fff" }}>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>هامش الربح المتوقّع</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: Number(advice.expected_profit_margin_pct) >= 0 ? "#16a34a" : "#dc2626" }}>{Number(advice.expected_profit_margin_pct).toFixed(1)}%</div>
                </div>
              )}
              {advice.expected_units_per_day !== undefined && advice.verdict !== "no" && (
                <div className="card" style={{ padding: 10, background: "#fff" }}>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>المبيعات اليومية المتوقّعة</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#6b4423" }}>{Number(advice.expected_units_per_day).toLocaleString("ar-SA-u-nu-latn")}</div>
                </div>
              )}
              {advice.expected_total_revenue !== undefined && advice.verdict !== "no" && (
                <div className="card" style={{ padding: 10, background: "#fff" }}>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>إجمالي الإيراد المتوقّع</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#0369a1" }}>{Number(advice.expected_total_revenue).toLocaleString("ar-SA-u-nu-latn")} ر</div>
                </div>
              )}
              {advice.expected_total_profit !== undefined && (
                <div className="card" style={{ padding: 10, background: "#fff" }}>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>إجمالي الربح المتوقّع</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: Number(advice.expected_total_profit) >= 0 ? "#16a34a" : "#dc2626" }}>{Number(advice.expected_total_profit).toLocaleString("ar-SA-u-nu-latn")} ر</div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 8 }}>
            {advice.target_audience && <InsightCard icon="🎯" label="الفئة المستهدفة" body={advice.target_audience} color="#7c3aed" />}
            {advice.best_timing && <InsightCard icon="⏰" label="التوقيت الأمثل" body={advice.best_timing} color="#0891b2" />}
            {advice.duration_recommendation && <InsightCard icon="📅" label="رأيي في مدة العرض" body={advice.duration_recommendation} color="#d97706" />}
            {advice.customer_behavior_insight && <InsightCard icon="👥" label="من سلوك العملاء" body={advice.customer_behavior_insight} color="#db2777" />}
            {advice.financial_impact && <InsightCard icon="💰" label="الأثر المالي" body={advice.financial_impact} color="#16a34a" />}
          </div>

          {(advice.saudi_calendar_factors || []).length > 0 && <ListCard title="🌙 عوامل التقويم السعودي" items={advice.saudi_calendar_factors} color="#7c3aed" />}
          {(advice.weather_factors || []).length > 0 && <ListCard title="☀️ عوامل الطقس" items={advice.weather_factors} color="#f59e0b" />}
          {(advice.terms_and_conditions || []).length > 0 && <ListCard title="📋 الشروط والأحكام المقترحة" items={advice.terms_and_conditions} color="#0891b2" />}
          {(advice.marketing_channels || []).length > 0 && <ListCard title="📢 قنوات التسويق الموصى بها" items={advice.marketing_channels} color="#db2777" />}
          {(advice.risks || []).length > 0 && <ListCard title="⚠️ المخاطر المحتملة" items={advice.risks} color="#dc2626" />}

          {advice.advice && (
            <div style={{ marginTop: 4, padding: 14, background: "#fff", borderRadius: 10, borderInlineStart: "4px solid #6b4423" }}>
              <div style={{ fontWeight: 800, color: "#6b4423", fontSize: 13, marginBottom: 6 }}>💬 نصيحتي النهائية</div>
              <div style={{ fontSize: 13, lineHeight: 1.8, color: "var(--brown-deep)", whiteSpace: "pre-wrap" }}>{advice.advice}</div>
            </div>
          )}
        </div>
      )}

      {!loading && !advice && !err && (
        <div style={{ padding: 18, textAlign: "center", color: "var(--muted)", fontSize: 12.5 }}>
          اضغط «🤖 استشر المستشار» لتحصل على تحليل شامل لهذا {plan_type === "combo" ? "الكومبو" : "العرض"}.
        </div>
      )}
    </div>
  );
}

function InsightCard({ icon, label, body, color }) {
  return (
    <div className="card" style={{ padding: 10, background: "#fff", borderInlineStart: `3px solid ${color}` }}>
      <div style={{ fontSize: 11, color, fontWeight: 700 }}>{icon} {label}</div>
      <div style={{ fontSize: 12.5, color: "var(--brown-deep)", marginTop: 4, lineHeight: 1.7 }}>{body}</div>
    </div>
  );
}

function ListCard({ title, items, color }) {
  return (
    <div className="card" style={{ padding: 10, background: "#fff", borderInlineStart: `3px solid ${color}` }}>
      <div style={{ fontSize: 12, color, fontWeight: 800, marginBottom: 6 }}>{title}</div>
      <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.8, color: "var(--brown-deep)" }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
